import React, { createContext, useMemo } from 'react'
import isEmpty from 'lodash/isEmpty'
import { useDebouncedCallback } from 'use-debounce'
import { PersonalPage } from '@chilipiper/api-client-workspace'
import { useUpdateSectionAndAutoSave } from '@chilipiper/meeting-type-components'
import { usePersonalPage } from '../../api/personal-details'
import { useSetPersonalPage } from '../../api/personal-details'

interface PersonalPageStateValue {
  canCreateNewPersonalPage: boolean
  isLoadingPersonalPage: boolean
  isLoadingUpdatePersonalPage: boolean
  personalPage?: PersonalPage
  setPersonalPage: (personalPage: Partial<PersonalPage>) => void
}

export const PersonalPageContext = createContext({} as PersonalPageStateValue)

interface Props {
  children: React.ReactNode
}

export const PersonalPageStateProvider = ({ children }: Props) => {
  const { data: fetchedPersonalPage, isLoading: isLoadingPersonalPage, isError } = usePersonalPage()
  const { mutate, isPending: isLoadingUpdatePersonalPage } = useUpdateSectionAndAutoSave(
    useSetPersonalPage,
    []
  )
  const debouncedUpdatePersonalPage = useDebouncedCallback(mutate, 300)
  const setPersonalPage = (personalPage: Partial<PersonalPage>) => {
    if (fetchedPersonalPage) {
      debouncedUpdatePersonalPage({
        ...fetchedPersonalPage,
        ...personalPage,
      } as PersonalPage)
    }
  }

  const canCreateNewPersonalPage = useMemo(
    () => isEmpty(fetchedPersonalPage) && !isLoadingPersonalPage && isError,
    [fetchedPersonalPage, isLoadingPersonalPage, isError]
  )

  const contextValue = useMemo(
    () => ({
      canCreateNewPersonalPage,
      personalPage: fetchedPersonalPage,
      setPersonalPage,
      isLoadingPersonalPage,
      isLoadingUpdatePersonalPage,
    }),
    [
      canCreateNewPersonalPage,
      fetchedPersonalPage,
      isLoadingPersonalPage,
      isLoadingUpdatePersonalPage,
      setPersonalPage,
    ]
  )

  return (
    <PersonalPageContext value={contextValue}>{children}</PersonalPageContext>
  )
}
